import { useEffect, useState } from 'react';
import { C2Button } from '../c2';

interface ConfirmDeleteDialogProps {
  open: boolean;
  title: string;
  description: string;
  /** Exact string the user must type to enable the confirm button. */
  confirmPhrase: string;
  /** Bulleted consequences shown below the description. */
  consequences?: string[];
  confirmLabel?: string;
  onConfirm: () => void | Promise<void>;
  onClose: () => void;
}

export default function ConfirmDeleteDialog({
  open,
  title,
  description,
  confirmPhrase,
  consequences,
  confirmLabel = 'Delete permanently',
  onConfirm,
  onClose,
}: ConfirmDeleteDialogProps) {
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setTyped('');
      setBusy(false);
      setError(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onClose]);

  if (!open) return null;

  const matches = typed === confirmPhrase;
  const canConfirm = matches && !busy;

  const handleConfirm = async () => {
    if (!canConfirm) return;
    setBusy(true);
    setError(null);
    try {
      await onConfirm();
    } catch (err) {
      setError((err as Error).message || 'Delete failed');
      setBusy(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={() => { if (!busy) onClose(); }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100,
        background: 'rgba(20, 18, 35, 0.42)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        className="bg-surface"
        style={{
          width: '100%',
          maxWidth: 460,
          borderRadius: 20,
          boxShadow: 'var(--shadow-lg)',
          padding: 28,
        }}
      >
        <h2
          className="font-display font-bold text-ink m-0 mb-2"
          style={{ fontSize: 22, letterSpacing: '-0.01em' }}
        >
          {title}
        </h2>
        <p className="text-[14px] text-ink-soft m-0 mb-3" style={{ lineHeight: 1.5 }}>
          {description}
        </p>

        {consequences && consequences.length > 0 && (
          <ul
            className="m-0 mb-4"
            style={{
              listStyle: 'none',
              padding: 14,
              border: '1px solid var(--color-hairline)',
              borderRadius: 12,
              background: 'var(--color-bg-alt)',
            }}
          >
            {consequences.map((c, i) => (
              <li
                key={i}
                className="text-[13px] text-ink-soft"
                style={{ padding: '2px 0', display: 'flex', gap: 8 }}
              >
                <span style={{ color: 'var(--color-peach-deep)' }}>•</span>
                <span>{c}</span>
              </li>
            ))}
          </ul>
        )}

        <label className="text-[12.5px] font-semibold text-ink block mb-1.5">
          Type{' '}
          <span
            className="font-mono"
            style={{
              background: 'var(--color-bg-alt)',
              padding: '1px 6px',
              borderRadius: 4,
              border: '1px solid var(--color-hairline)',
              fontSize: 12,
            }}
          >
            {confirmPhrase}
          </span>{' '}
          to confirm
        </label>
        <input
          autoFocus
          value={typed}
          onChange={e => setTyped(e.target.value)}
          disabled={busy}
          autoComplete="off"
          spellCheck={false}
          className="font-mono w-full outline-none"
          style={{
            padding: '10px 14px',
            border: '1px solid var(--color-hairline-strong)',
            borderRadius: 12,
            fontSize: 14,
            background: 'var(--color-surface)',
            color: 'var(--color-ink)',
          }}
        />

        {error && (
          <div
            className="mt-3 text-[13px]"
            style={{
              color: 'var(--color-peach-deep)',
              background: 'var(--color-peach)',
              padding: '8px 12px',
              borderRadius: 10,
            }}
          >
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 mt-5">
          <C2Button variant="outline" size="sm" onClick={onClose} disabled={busy}>
            Cancel
          </C2Button>
          <button
            onClick={() => { void handleConfirm(); }}
            disabled={!canConfirm}
            className="inline-flex items-center justify-center font-semibold rounded-pill font-body whitespace-nowrap transition-all duration-150"
            style={{
              padding: '8px 18px',
              fontSize: 13,
              background: canConfirm ? 'var(--color-peach-deep)' : 'var(--color-hairline-strong)',
              color: '#fff',
              border: 0,
              cursor: canConfirm ? 'pointer' : 'not-allowed',
            }}
          >
            {busy ? 'Deleting…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
